/**
 * Pinned + recently opened places, for the sidebar's quick menu.
 *
 * Kept in this browser (localStorage): it's a per-person convenience, so a
 * cleared browser just starts fresh. Pages and records report themselves with
 * `trackRecent()` (lists from the sidebar's route watcher, records from their
 * own loaders once the name is known). Accounts, the Funnel board and the
 * Calendar are pinned until unpinned.
 */
import { useSyncExternalStore } from "react";

export type RecentKind = "page" | "person" | "account" | "affiliate" | "partner" | "creator" | "source" | "campaign" | "automation";

export interface RecentItem {
  /** Path inside the app, e.g. "/contacts/<id>". The identity of an item. */
  to: string;
  label: string;
  kind: RecentKind;
  /** When it was last opened (ms). */
  at: number;
}

const RECENT_KEY = "cc-recent-v1";
const PINNED_KEY = "cc-pinned-v1";
const MAX_RECENT = 15;
const DEFAULT_PINNED: RecentItem[] = [
  { to: "/accounts", label: "Accounts", kind: "page", at: 0 },
  { to: "/funnel", label: "Funnel board", kind: "page", at: 0 },
  { to: "/work/calendar", label: "Calendar", kind: "page", at: 0 },
];
// Pins added to the defaults later reach browsers that already saved their
// list, once each (unpinning them afterwards sticks).
const SEEDED_KEY = "cc-pinned-seeded";
const SEEDED_PINS = ["/funnel", "/work/calendar"];

interface State { recent: RecentItem[]; pinned: RecentItem[] }

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}
function write(key: string, value: unknown) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* private mode: keep it in memory */ }
}

const valid = (x: unknown): x is RecentItem =>
  !!x && typeof x === "object" && typeof (x as RecentItem).to === "string" && typeof (x as RecentItem).label === "string";

let state: State = {
  recent: read<unknown[]>(RECENT_KEY, []).filter(valid),
  pinned: (() => {
    const p = read<unknown[] | null>(PINNED_KEY, null);
    if (!Array.isArray(p)) return DEFAULT_PINNED;
    const saved = p.filter(valid);
    const seeded = read<string[]>(SEEDED_KEY, []);
    const add = DEFAULT_PINNED.filter((d) => SEEDED_PINS.includes(d.to) && !seeded.includes(d.to) && !saved.some((s) => s.to === d.to));
    if (SEEDED_PINS.some((to) => !seeded.includes(to))) {
      write(SEEDED_KEY, SEEDED_PINS);
      if (add.length) write(PINNED_KEY, [...saved, ...add]);
    }
    return [...saved, ...add];
  })(),
};
const listeners = new Set<() => void>();
const emit = () => { for (const l of listeners) l(); };
const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };

/** Note that a page or record was opened (moves it to the top). */
export function trackRecent(item: Omit<RecentItem, "at">) {
  if (!item.to || !item.label.trim()) return;
  const now = Date.now();
  const top = state.recent[0];
  // Same place, same name, a moment ago: nothing to change (avoids re-renders on every load).
  if (top && top.to === item.to && top.label === item.label && now - top.at < 5_000) return;
  const recent = [{ ...item, at: now }, ...state.recent.filter((r) => r.to !== item.to)].slice(0, MAX_RECENT);
  // A pinned record keeps its newest name.
  const pinned = state.pinned.map((p) => (p.to === item.to && item.kind !== "page" ? { ...p, label: item.label } : p));
  state = { recent, pinned };
  write(RECENT_KEY, recent);
  write(PINNED_KEY, pinned);
  emit();
}

export function isPinned(to: string) {
  return state.pinned.some((p) => p.to === to);
}

export function togglePin(item: Omit<RecentItem, "at">) {
  const pinned = isPinned(item.to)
    ? state.pinned.filter((p) => p.to !== item.to)
    : [...state.pinned, { ...item, at: Date.now() }];
  state = { ...state, pinned };
  write(PINNED_KEY, pinned);
  emit();
}

/** Forget a record that no longer exists (deleted person, removed source). */
export function forgetRecent(to: string) {
  state = { recent: state.recent.filter((r) => r.to !== to), pinned: state.pinned.filter((p) => p.to !== to) };
  write(RECENT_KEY, state.recent);
  write(PINNED_KEY, state.pinned);
  emit();
}

export function clearRecent() {
  state = { ...state, recent: [] };
  write(RECENT_KEY, []);
  emit();
}

export function useQuickPlaces(): State {
  return useSyncExternalStore(subscribe, () => state, () => state);
}
